import { Card, Grid, Theme, Typography, useTheme } from "@mui/material";
import { useCallback, useEffect, useMemo, useState } from "react";
// Redux Forms
import {
  getDefaultPlannedCreditMap,
  IFlight,
  IFlightType,
  IGroundEvent,
  INewTemplateFlight,
  IPairingPositionCounts,
  IPlannedCreditMap,
} from "@vmosolutions/oc-interfaces";
import _ from "lodash";
// Flights Table
import { v1 } from "uuid";
import { IBuildPayload, IUpdateTableEvents } from "./BuildStatus/BuildStatus";
import { BulkPairingInfo } from "./BulkPairingInfo";
import { EventTypes } from "./FlightBuilder";
import { getInitialValues, validateFlight } from "./Forms/FlightRowEdit";
import { FlightRows } from "./Forms/FlightRows";
import { IRootState } from "src/redux/reducers/CombinedReducers";
import { useSelector } from "react-redux";

interface IFlightsProps {
  id: string;
  path: string[];
  eventType: { value: EventTypes; label: string };
  setFocusOnEvent?: (event: IFlight | IGroundEvent) => void;
  sendBuildStatus?: (buildPayload: IBuildPayload) => void;
  updateTableEvents: IUpdateTableEvents;
  defaultFlightType?: IFlightType;
  startDate: number;
  buildAsLocal: boolean;
  setBuildAsLocal: (state: boolean) => void;
}

const FlightBuilderFormStyles = (theme: Theme): any => ({
  height: "fit-content",
  padding: "10px 7px",
  minWidth: 1120,
  marginTop: "10px",
  "& .formContainer": {
    "& .pairing_creator_info_card, & .flights_form_card, & .recurring_flights_form_card, & .ground_events_form_card": {
      flexGrow: 1,
      boxShadow: theme.shadows[5],
      margin: "8px",
      backgroundColor: theme.custom.timeline.contextListBackground,
    },
    "& .time_mode_selector": {
      justifyContent: "flex-start",
    },
  },
  "& .tableContainer": {
    borderRadius: "5px",
    marginLeft: "8px",
    marginTop: "6px",
    flexGrow: 1,
    boxShadow: theme.shadows[5],
    backgroundColor: theme.custom.timeline.contextListBackground,
  },
});

// Strip out any invalid flights before sending to build status tool.
const showOnlyValidFlights = (flights: INewTemplateFlight[]): INewTemplateFlight[] => {
  return flights.filter((f) => !!f?._id.includes("temp") && Object.entries(validateFlight(f))?.length === 0);
};

/**
 * CREATE FLIGHT COMPONENT
 */
export const Flights = (props: IFlightsProps): JSX.Element => {
  const theme: Theme = useTheme();
  const {
    id,
    eventType,
    sendBuildStatus,
    updateTableEvents,
    defaultFlightType,
    startDate,
    buildAsLocal,
    setBuildAsLocal,
  } = props;

  // Additional Fields (do not include these in the form.
  const [pairingPosCounts, setPairingPosCounts] = useState<IPairingPositionCounts>(undefined);
  const [includeSplits, setIncludeSplits] = useState<boolean>(false);
  const [isBiddable, setIsBiddable] = useState<boolean>(false);
  const [plannedCredit, setPlannedCredit] = useState<number>(0);
  const [plannedCreditMap, setPlannedCreditMap] = useState<IPlannedCreditMap>(undefined);
  const creditSettingValue = useSelector((state: IRootState) => state.credit);
  const creditSetting = useMemo(() => {
    return creditSettingValue.settings ?? [];
  }, [creditSettingValue]);

  const initialPlannedCreditMap = useMemo<IPlannedCreditMap>(() => {
    if (creditSetting && creditSetting.length > 0) {
      const defaultMap = getDefaultPlannedCreditMap(creditSetting);
      return defaultMap;
    }
    return undefined;
  }, [creditSetting]);

  useEffect(() => {
    if (creditSetting && creditSetting.length > 0) {
      const defaultMap = getDefaultPlannedCreditMap(creditSetting);
      setPlannedCreditMap(defaultMap);
    }
  }, [creditSetting]);

  const [quickFlights, setQuickFlights] = useState<INewTemplateFlight[]>([getInitialValues(defaultFlightType)]);

  const validFlights = useMemo(() => showOnlyValidFlights(quickFlights), [quickFlights]);

  // As the status sends back info on successfully updated flights change their temp ids to actuals.
  useEffect(() => {
    if (
      !updateTableEvents ||
      (updateTableEvents.hasOwnProperty("cleanOut") && Object.keys(updateTableEvents)?.length === 1)
    ) {
      return;
    }
    // If resetAll is passed, reset all ids back to temps.
    if (updateTableEvents.resetAll) {
      setQuickFlights((current) => [...current].map((f) => ({ ...f, _id: `temp-${v1()}` })));
    } else {
      setQuickFlights((current) => {
        if (!updateTableEvents?.cleanOut) {
          // flight successfully built, leave in table but replace it's temp id.
          return current.map((event) => {
            if (updateTableEvents[event._id]) {
              return { ...event, _id: updateTableEvents[event._id] };
            }
            return event;
          });
        } else {
          // REMOVE any flights that have successfully been build.
          return current.filter((event) => !updateTableEvents[event._id]);
        }
      });
    }
  }, [updateTableEvents]);

  //TODO Add a modal to this call so that users can't accidentally remove a flight.
  /**
   * Remove a selected flight leg
   */
  const removeFlightLeg = useCallback(
    (id: string): void => {
      const updatedFlights = [...quickFlights];
      const findIndex = updatedFlights?.findIndex((f) => f?._id == id);
      if (findIndex > -1) {
        updatedFlights.splice(findIndex, 1);
        // updatedFlights.sort((a, b) => (a?.scheduledOut as number) - (b?.scheduledOut as number));
        setQuickFlights(updatedFlights);
      }
    },
    [quickFlights]
  );

  /**
   * Add a New flight leg
   */
  const addFlightLeg = useCallback((): void => {
    const lastFlight = quickFlights[quickFlights?.length - 1];
    const newLeg = {
      ...getInitialValues(defaultFlightType),
      ...{
        flightNumber: lastFlight?.flightNumber, // prefill
        origin: lastFlight?.destination, // prefill original with last flights destination
        flightType: lastFlight?.flightType, // prefill
        scheduledOut: lastFlight?.scheduledIn ?? "00:00", // prefill scheduled out with last flights scheduled in.
        aircraft: lastFlight?.aircraft, // prefill
        aircraftConfiguration: lastFlight?.aircraftConfiguration, // prefill
      },
    };
    setQuickFlights([...quickFlights, newLeg]);
  }, [defaultFlightType, quickFlights]);

  /**
   * Function fired off if user updated a selected flight leg.
   */
  const updateFlightLeg = useCallback(
    (formValues: INewTemplateFlight): void => {
      const updatedFlights = [...quickFlights];
      const findIndex = updatedFlights?.findIndex((f) => f?._id == formValues?._id);
      if (findIndex > -1) {
        updatedFlights[findIndex] = formValues;
        // updatedFlights?.sort((a, b) => (a?.scheduledOut as number) - (b?.scheduledOut as number));
        setQuickFlights(updatedFlights);
      }
    },
    [quickFlights]
  );

  // Update Build Status payload and sent to Build Status component.
  // Create deep clone of the flights array (avoids mutation by the status tool)
  useEffect(() => {
    const events = _.cloneDeep(validFlights);
    sendBuildStatus({
      eventPayload: events,
      pairingPositionCounts: pairingPosCounts,
      includeSplits: includeSplits,
      isBiddable,
      plannedCredit,
      plannedCreditMap,
    });
  }, [
    validFlights,
    sendBuildStatus,
    pairingPosCounts,
    includeSplits,
    startDate,
    isBiddable,
    plannedCredit,
    plannedCreditMap,
  ]);

  // Reset all changes to template legs
  const resetAllChangesToLegs = useCallback(() => {
    setQuickFlights([getInitialValues(defaultFlightType)]);
  }, [defaultFlightType]);

  return (
    <Grid container sx={FlightBuilderFormStyles(theme)} direction="column" wrap="nowrap">
      <Grid item className={"formContainer"} style={{ width: 1215 }}>
        <FlightRows
          mosaicId={id}
          flightLegs={quickFlights}
          legsAreDirty={validFlights.length > 0}
          resetAllChangesToLegs={resetAllChangesToLegs}
          onSubmit={updateFlightLeg}
          addFlightLeg={addFlightLeg}
          removeFlightLeg={removeFlightLeg}
          eventType={eventType.value}
          buildAsLocal={buildAsLocal}
          setBuildAsLocal={setBuildAsLocal}
          startDate={startDate}
        />
      </Grid>

      <Card className={"tableContainer"} style={{ width: 1200 }}>
        <Grid container direction="column" style={{ width: "100%" }}>
          <Typography variant="h6" sx={{ fontSize: "0.9em", margin: "3px 10px" }}>
            Pairings
          </Typography>
          <BulkPairingInfo
            initialPairingPosCounts={undefined}
            pairingPosCounts={pairingPosCounts}
            setPairingPosCounts={setPairingPosCounts}
            includeSplits={includeSplits}
            initialIncludeSplits={false}
            setIncludeSplits={setIncludeSplits}
            initialIsBiddable={false}
            isBiddable={isBiddable}
            setIsBiddable={setIsBiddable}
            initialPlannedCredit={0}
            plannedCredit={plannedCredit}
            setPlannedCredit={setPlannedCredit}
            hidePlannedCredit={creditSettingValue.hidePlannedCreditField}
            initialPlannedCreditMap={initialPlannedCreditMap}
            plannedCreditMap={plannedCreditMap}
            setPlannedCreditMap={setPlannedCreditMap}
          />
        </Grid>
      </Card>
    </Grid>
  );
};
